import React from 'react'
import { Link } from 'react-router-dom'
import { companyInfo } from '../data'
import SubpageHero from '../components/SubpageHero'
import useMeta from '../hooks/useMeta'

export default function AboutPage() {
  useMeta(
    'Om os',
    'Læs mere om Matheus Cleaning Service. Vi leverer professionel rengøring til private og erhverv med fokus på kvalitet, tillid og miljøvenlige metoder.'
  )

  const heroImages = [
    'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/friendly-woman-cleaning-scandinavian-home-hero-v3.jpg',
    'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/professional-cleaning-in-action-no-logo.jpg',
    'https://assets.auxodev.dk/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/premium-hero-cleaning-background-no-logo.jpg'
  ]

  return (
    <div className="flex flex-col min-h-screen bg-brand-light">
      {/* Hero Section */}
      <SubpageHero
        title="Professionel rengøring med hjerte og kvalitet"
        intro="Lær Matheus Cleaning Service at kende. Vi er dit trygge valg til privat rengøring, housekeeping og erhvervsrengøring i hele Storkøbenhavn."
        images={heroImages}
      />

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Text Column */}
            <div className="lg:col-span-7 text-left space-y-6">
              <h2 className="text-2xl md:text-3xl font-bold text-brand-navy tracking-tight">
                Vores historie og vision
              </h2>
              <p className="text-base text-brand-text-muted leading-relaxed">
                Matheus Cleaning Service blev grundlagt med en klar vision: At hæve barren for, hvad man kan forvente af et professionelt rengøringsfirma. Vi mener, at et rent hjem eller et sundt arbejdsmiljø ikke blot handler om det visuelle resultat, men om den tryghed, sundhed og livskvalitet, det bringer med sig.
              </p>
              <p className="text-base text-brand-text-muted leading-relaxed">
                Vi har fra dag ét fokuseret på at opbygge langvarige relationer med vores kunder. Det gør vi gennem kompromisløs grundighed, stabil service og en gennemskuelig prisstruktur. Uanset om vi besøger et privat hjem eller en stor erhvervsvirksomhed, møder vi altid op med samme høje engagement og professionalisme.
              </p>
              <p className="text-base text-brand-text-muted leading-relaxed">
                Vores team består af dedikerede og veluddannede rengøringsassistenter, der alle deler vores passion for renhed og god service. Vi passer på dine lokaler, som var de vores egne, og sikrer et fejlfrit resultat hver eneste gang.
              </p>
              <div className="pt-4">
                <Link
                  to="/kontakt"
                  className="bg-black text-white hover:bg-neutral-800 px-6 py-3.5 text-sm font-bold inline-block transition-all duration-200"
                >
                  Kontakt os
                </Link>
              </div>
            </div>

            {/* Image Column */}
            <div className="lg:col-span-5">
              <div className="w-full h-[350px] md:h-[450px] overflow-hidden">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/professional-cleaning-in-action-no-logo.jpg"
                  alt="Professionel rengøring i gang"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center max-w-5xl mx-auto">
            <div className="space-y-2">
              <p className="text-4xl md:text-5xl font-extrabold text-brand-navy tracking-tight">100%</p>
              <h4 className="text-base font-bold text-brand-navy">Tryghedsgaranti</h4>
              <p className="text-sm text-brand-text-muted leading-relaxed max-w-xs mx-auto">
                Fuld ansvarsforsikring og medarbejdere med ren straffeattest.
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-4xl md:text-5xl font-extrabold text-brand-navy tracking-tight">500+</p>
              <h4 className="text-base font-bold text-brand-navy">Glade kunder</h4>
              <p className="text-sm text-brand-text-muted leading-relaxed max-w-xs mx-auto">
                Faste aftaler med både private husstande og erhvervsvirksomheder.
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-4xl md:text-5xl font-extrabold text-brand-navy tracking-tight">0 kr.</p>
              <h4 className="text-base font-bold text-brand-navy">Skjulte gebyrer</h4>
              <p className="text-sm text-brand-text-muted leading-relaxed max-w-xs mx-auto">
                Altid gennemskuelige priser og faste aftaler uden uventede tillæg.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-brand-navy text-white py-16 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(0,102,255,0.15),transparent_50%)]" />
        <div className="mx-auto max-w-4xl px-6 relative z-10">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
            Skal vi hjælpe dig med rengøringen?
          </h2>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Udfyld vores kontaktformular, eller ring til os direkte. Vi vender hurtigt tilbage med et uforpligtende tilbud tilpasset dine ønsker.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="bg-white text-brand-navy px-6 py-3.5 text-sm font-bold transition-all duration-200"
            >
              Få et uforpligtende tilbud
            </Link>
            <a
              href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`}
              className="bg-transparent px-6 py-3.5 text-sm font-bold text-white border border-white transition-all duration-200 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone" aria-hidden="true"></i> {companyInfo.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
